import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { ModalProps } from "@mui/material";
import { ReactNode } from "react";
import { SYMBOL } from "../../helpers/constants";
import { COMPONENT_IDS } from "../../helpers/constants/componentIds";
import { isNotEmpty } from "../../helpers/utils";
import { useMediaQueries } from "../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../hooks/useNavigationSiteCore";
import { MuiBox } from "../box/MuiBox";
import {
  MuiButtonPrimary,
  MuiButtonSecondary,
  MuiButtonTertiary,
  MuiIconButton,
} from "../button/MuiButton";
import { ErrorCrossIcon } from "../errorCrossIcon/ErrorCrossIcon";
import { MuiTypography } from "../typography/MuiTypography";
import { MuiModalWrapper } from "./MuiModal.style";
import MuiEllipseLabel from "../ellipseLabel/MuiEllipseLabel";

interface MuiModalProps extends ModalProps {
  onClose: () => void;
  title: string;
  primaryButtonTitle?: string;
  secondaryButtonTitle?: string;
  tertiaryButtonTitle?: string;
  onPrimaryButtonClick?: () => void;
  onSecondaryButtonClick?: () => void;
  onTertiaryButtonClick?: () => void;
  disablePrimaryButton?: boolean;
  hasLargeContent?: boolean;
  showPrimaryButton?: boolean;
  className?: string;
  headerChildren?: ReactNode;
  showSecondaryButton?: boolean;
  footerErrorMessage?: string;
  actionButtonTitle?: string;
  onActionButtonClick?: () => void;
  disableActionButton?: boolean;
  actionButtonIcon?: ReactNode;
  stackButtonsOnMobile?: boolean;
  disableSecondaryButton?: boolean;
  leftActionButtonTitle?: string;
  leftActionButtonIcon?: ReactNode;
  leftActionButtonClick?: () => void;
}

export const MuiModal = ({
  open,
  onClose,
  children,
  title,
  primaryButtonTitle,
  secondaryButtonTitle,
  tertiaryButtonTitle,
  onPrimaryButtonClick,
  onSecondaryButtonClick,
  onTertiaryButtonClick,
  disablePrimaryButton = false,
  hasLargeContent = false,
  showPrimaryButton = true,
  className = "",
  headerChildren,
  showSecondaryButton = true,
  id = COMPONENT_IDS.COMMON_MODAL,
  footerErrorMessage,
  actionButtonTitle,
  onActionButtonClick,
  disableActionButton = false,
  actionButtonIcon,
  stackButtonsOnMobile = false,
  disableSecondaryButton,
  leftActionButtonTitle,
  leftActionButtonIcon,
  leftActionButtonClick,
}: MuiModalProps) => {
  const handleCloseIconClick = () => {
    onClose();
  };

  const { commonARIALabel } = useNavigationSiteCore();
  const { isMobile } = useMediaQueries();

  const renderMobile = () => {
    const firstHyphenIndex = title.indexOf(SYMBOL.MINUS);
    const mainTitle =
      firstHyphenIndex !== -1 ? title.slice(0, firstHyphenIndex).trim() : title;
    const subTitle =
      firstHyphenIndex !== -1 ? title.slice(firstHyphenIndex + 1).trim() : "";

    return (
      <MuiBox className="parent-hierarchy-box">
        <MuiTypography
          variant="h2SemiBold"
          component="h2"
          aria-hidden
          id={COMPONENT_IDS.COMMON_MODAL_TITLE}
          data-testid={COMPONENT_IDS.COMMON_MODAL_TITLE}
          className="ellipsis-title"
        >
          {mainTitle}
        </MuiTypography>
        <MuiTypography
          variant="body3"
          component="body"
          className="subtitle-text"
        >
          {subTitle}
        </MuiTypography>
      </MuiBox>
    );
  };

  const renderWeb = () => {
    return (
      <MuiTypography
        aria-hidden
        id={COMPONENT_IDS.COMMON_MODAL_TITLE}
        data-testid={COMPONENT_IDS.COMMON_MODAL_TITLE}
        variant="h2SemiBold"
        component="h2"
        className="text-title"
      >
        <MuiEllipseLabel text={title} labelTypoVariant="h2SemiBold" />
      </MuiTypography>
    );
  };

  return (
    <MuiModalWrapper
      data-testid={id}
      id={id}
      open={open}
      onClose={onClose}
      role="dialog"
      aria-labelledby={COMPONENT_IDS.COMMON_MODAL_TITLE}
    >
      <MuiBox
        role="dialog"
        aria-labelledby={id}
        className={`sub-container ${hasLargeContent && "large-sub-container"} ${className}`}
      >
        <MuiBox className={`header ${hasLargeContent && "large-header"}`}>
          {isMobile && title.includes("-") ? renderMobile() : renderWeb()}
          <MuiBox className="btn-container">
            <MuiBox aria-hidden>{headerChildren}</MuiBox>
            <MuiIconButton
              onClick={handleCloseIconClick}
              data-testid={COMPONENT_IDS.COMMON_MODAL_CLOSE_ICON}
              aria-label={commonARIALabel.closeModalWindow}
            >
              <CloseRoundedIcon className="close-image" />
            </MuiIconButton>
          </MuiBox>
        </MuiBox>
        <MuiBox
          className={`modal-container ${hasLargeContent && "add-overflow"}`}
        >
          {children}
        </MuiBox>
        <MuiBox
          className={`modal-button-container ${hasLargeContent ? "large-button-container" : ""} ${stackButtonsOnMobile ? "responsive-button-container" : ""}`}
        >
          {leftActionButtonTitle && isNotEmpty(leftActionButtonTitle) && (
            <MuiButtonTertiary
              onClick={leftActionButtonClick}
              className="full-width"
              startIcon={leftActionButtonIcon}
              data-testid={COMPONENT_IDS.COMMON_MODAL_ACTION_BTN}
            >
              {leftActionButtonTitle}
            </MuiButtonTertiary>
          )}

          {actionButtonTitle && isNotEmpty(actionButtonTitle) && (
            <MuiButtonTertiary
              onClick={onActionButtonClick}
              className="full-width action-tertiary"
              disabled={disableActionButton}
              startIcon={actionButtonIcon || <ArrowBackIosIcon />}
              data-testid={COMPONENT_IDS.COMMON_MODAL_ACTION_BTN}
            >
              {actionButtonTitle}
            </MuiButtonTertiary>
          )}
          <MuiBox>
            {isNotEmpty(footerErrorMessage) && (
              <MuiBox className="bottom-children">
                <MuiBox className="error-container">
                  <ErrorCrossIcon className="error-cross-icon" />
                  <MuiTypography
                    variant="h4Medium"
                    className="error"
                    data-testid={COMPONENT_IDS.COMMON_MODAL_FOOTER_ERROR}
                  >
                    {footerErrorMessage}
                  </MuiTypography>
                </MuiBox>
              </MuiBox>
            )}
          </MuiBox>
          <MuiBox className="btn-sub-container">
            {isNotEmpty(tertiaryButtonTitle) && (
              <MuiButtonTertiary
                onClick={onTertiaryButtonClick}
                className="full-width tertiary"
                id={COMPONENT_IDS.COMMON_MODAL_TERTIARY_BTN}
              >
                {tertiaryButtonTitle}
              </MuiButtonTertiary>
            )}
            {showSecondaryButton && isNotEmpty(secondaryButtonTitle) && (
              <MuiButtonSecondary
                onClick={onSecondaryButtonClick}
                className="full-width"
                disabled={disableSecondaryButton}
                id={COMPONENT_IDS.COMMON_MODAL_SECONDARY_BTN}
              >
                {secondaryButtonTitle}
              </MuiButtonSecondary>
            )}

            {showPrimaryButton && isNotEmpty(primaryButtonTitle) && (
              <MuiButtonPrimary
                onClick={onPrimaryButtonClick}
                className="full-width"
                disabled={disablePrimaryButton}
                id={COMPONENT_IDS.COMMON_MODAL_PRIMARY_BTN}
              >
                {primaryButtonTitle}
              </MuiButtonPrimary>
            )}
          </MuiBox>
        </MuiBox>
      </MuiBox>
    </MuiModalWrapper>
  );
};
